import type { SignalId } from "@repo/app-types/entities/signal";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import {
  useAllSignalsQuery,
  useDeleteSignalMutation,
} from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageLayout } from "../../../components/layout/pageLayout.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { formatSignalPlaybookScopeLabel } from "./signalPlaybookScopeField.js";

export const SignalListPage: FC = () => {
  const { data: signals, isLoading } = useAllSignalsQuery();
  const [deleteSignal] = useDeleteSignalMutation();
  const navigate = useNavigate();

  const [signalIdToDelete, setSignalIdToDelete] = useState<
    SignalId | undefined
  >(undefined);

  const renderRows = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!signals || signals.length === 0) {
      return (
        <SettingsTableEmptyBodyContent colSpan={2}>
          No signals have been created yet.
        </SettingsTableEmptyBodyContent>
      );
    }

    return signals.map((signal) => (
      <tr
        key={signal.id}
        className="group relative cursor-pointer hover:bg-gray-50"
        onClick={(event) => {
          if (event.defaultPrevented) {
            return;
          }
          navigate(appPaths.settingsSignal.build({ signalId: signal.id }));
        }}
      >
        <SettingsTableDataCell>{signal.name}</SettingsTableDataCell>
        <SettingsTableDataCell>
          <span className="text-muted-foreground">
            {formatSignalPlaybookScopeLabel(signal.playbook_scope)}
          </span>

          <div
            className="absolute top-1/2 right-3 hidden -translate-y-1/2 space-x-2 text-muted-foreground group-hover:flex"
            onClick={(e) => e.preventDefault()}
          >
            <Can I={PermissionActions.DELETE} a={PermissionSubjects.SIGNALS}>
              <IconButton
                variant={ButtonVariant.Secondary}
                icon={<IconTrash className="h-4 w-4" />}
                onClick={() => setSignalIdToDelete(signal.id)}
              />
            </Can>
          </div>
        </SettingsTableDataCell>
      </tr>
    ));
  };

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Form}
        title={settingsSections.signals.label}
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.SIGNALS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                icon={<IconPlus className="size-4" />}
                isDisabled={!allowed}
                onClick={() => navigate(appPaths.settingsNewSignal.build())}
              >
                Create signal
              </Button>
            )}
          </Can>
        }
      />

      <SimpleModal
        isOpen={!!signalIdToDelete}
        onCancel={() => setSignalIdToDelete(undefined)}
        variant={SimpleModalVariant.Warning}
        title="Delete signal"
        subtitle="Are you sure you want to delete this signal? This action can't be undone."
        confirmButtonText="Delete signal"
        onConfirm={() => {
          if (signalIdToDelete) {
            deleteSignal(signalIdToDelete);
          }
          setSignalIdToDelete(undefined);
        }}
      />
      <SettingsSection>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Define conversational patterns to detect in calls. Signals identify
            business-relevant moments like competitor mentions, budget
            questions, or objections.
          </div>

          <SettingsTable>
            <SettingsTableHeaderSection>
              <tr>
                <SettingsTableHeaderCell className="w-[60%]">
                  Name
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell>Applies to</SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>{renderRows()}</SettingsTableBodySection>
          </SettingsTable>
        </div>
      </SettingsSection>
    </PageLayout>
  );
};
